import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Phone, WhatsappLogo, EnvelopeSimple, House } from "@phosphor-icons/react";

/**
 * SocialSharePage — pagina pubblica `/r/:shareId`.
 *
 * Atterraggio per chi riceve un link condiviso dall'agente (su WhatsApp,
 * email, ecc.). Mostra il video MP4 generato dal Social Studio in player
 * nativo + dati base dell'immobile + CTA per contattare l'agente.
 *
 * NIENTE auth: usa solo l'endpoint pubblico /api/public/social/{id}.mp4 e
 * il nuovo /api/public/social/{id}/info.json per i metadati.
 *
 * Comportamento errori:
 *   • 410 (revocato/scaduto)  → "Questo link non è più disponibile"
 *   • 404                     → "Link non valido"
 *   • errori di rete          → fallback toast-style
 *
 * Tracking: il view_count è già incrementato lato server quando il browser
 * scarica il video. Niente analytics extra qui.
 */
export default function SocialSharePage() {
  const { shareId } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!shareId) return;
    (async () => {
      try {
        const resp = await fetch(`/api/public/social/${shareId}/info.json`, {
          headers: { Accept: "application/json" },
        });
        if (resp.status === 410) {
          setError({ code: 410, msg: "Questo link non è più disponibile" });
        } else if (resp.status === 404) {
          setError({ code: 404, msg: "Link non valido" });
        } else if (!resp.ok) {
          setError({ code: resp.status, msg: "Errore caricamento" });
        } else {
          setInfo(await resp.json());
        }
      } catch (e) {
        setError({ code: 0, msg: "Connessione fallita. Riprova." });
      } finally {
        setLoading(false);
      }
    })();
  }, [shareId]);

  if (loading) {
    return <Frame><div className="text-[#9A968B] text-[13px]">Carico…</div></Frame>;
  }
  if (error) {
    return (
      <Frame>
        <div className="text-center max-w-[400px]">
          <div className="text-[40px] mb-2 select-none">🔗</div>
          <h1 className="font-editorial text-[26px] text-[#1C1C1A] mb-2">{error.msg}</h1>
          <p className="text-[13px] text-[#6B6A65] mb-5">
            {error.code === 410
              ? "L'agente potrebbe averlo revocato o sono passati più di 7 giorni."
              : "Il link che hai ricevuto non è corretto o è stato rimosso."}
          </p>
          <Link to="/" className="inline-flex items-center gap-1.5 text-[#7C8B6E] hover:text-[#5C6E50] text-[13px] underline underline-offset-4">
            Vai alla home
          </Link>
        </div>
      </Frame>
    );
  }

  return <SharePlayer shareId={shareId} info={info} />;
}

function SharePlayer({ shareId, info }) {
  const { property, agency, asset_type } = info;
  const videoUrl = `/api/public/social/${shareId}.mp4`;

  const phone = (agency?.agent_phone || "").replace(/\s/g, "");
  const waPhone = phone.replace(/[^0-9]/g, "");
  const waText = encodeURIComponent(
    `Ciao ${agency?.agent_name || ""}, ho visto il video di ${property?.title || "l'immobile"} e vorrei più informazioni.`,
  );

  return (
    <Frame>
      <div className="w-full max-w-[440px] mx-auto" data-testid="share-player">
        {/* Player video 9:16 in cornice "phone" */}
        <div className="relative mx-auto rounded-[20px] overflow-hidden bg-black shadow-[0_20px_60px_-20px_rgba(0,0,0,0.4)]"
             style={{ aspectRatio: "9 / 16" }}>
          <video
            src={videoUrl}
            controls
            playsInline
            autoPlay
            muted
            loop
            className="w-full h-full object-cover"
            data-testid="share-video"
          />
        </div>

        {/* Property card */}
        <div className="mt-4 bg-white rounded-lg p-4 border border-[#E8E5DE]">
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#7C8B6E] mb-1">
            {asset_type === "before_after" ? "Prima · Dopo" : "Reel"}
          </div>
          <h1 className="font-editorial text-[22px] text-[#1C1C1A] leading-tight mb-1">
            {property?.title || "Immobile"}
          </h1>
          {property?.city && (
            <div className="text-[12.5px] text-[#6B6A65]">{property.city}</div>
          )}
          {property?.price && (
            <div className="text-[16px] font-semibold text-[#1C1C1A] mt-2">
              {new Intl.NumberFormat("it-IT").format(property.price)} €
            </div>
          )}
        </div>

        {/* CTA Contatta l'agente */}
        {(phone || agency?.agent_email) && (
          <div className="mt-3 bg-white rounded-lg p-4 border border-[#E8E5DE]">
            <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#7C8B6E] mb-2">
              Contatta {agency?.agent_name ? agency.agent_name.split(" ")[0] : "l'agente"}
            </div>
            {agency?.name && (
              <div className="text-[12.5px] text-[#6B6A65] mb-3">{agency.name}</div>
            )}
            <div className="grid grid-cols-1 gap-2">
              {waPhone && (
                <a href={`https://wa.me/${waPhone}?text=${waText}`} target="_blank" rel="noreferrer"
                   className="flex items-center gap-2 px-3 py-2.5 bg-[#25D366] hover:bg-[#1EA952] text-white font-medium rounded text-[13px] transition-colors"
                   data-testid="share-cta-whatsapp">
                  <WhatsappLogo size={16} weight="fill" /> WhatsApp
                </a>
              )}
              {phone && (
                <a href={`tel:${phone}`}
                   className="flex items-center gap-2 px-3 py-2.5 border border-[#E8E5DE] hover:border-[#7C8B6E] hover:bg-[#FAFAF7] text-[#1C1C1A] font-medium rounded text-[13px] transition-colors"
                   data-testid="share-cta-call">
                  <Phone size={16} weight="fill" className="text-[#7C8B6E]" /> Chiama
                </a>
              )}
              {agency?.agent_email && (
                <a href={`mailto:${agency.agent_email}?subject=${encodeURIComponent("Info " + (property?.title || "immobile"))}`}
                   className="flex items-center gap-2 px-3 py-2.5 border border-[#E8E5DE] hover:border-[#7C8B6E] hover:bg-[#FAFAF7] text-[#1C1C1A] font-medium rounded text-[13px] transition-colors"
                   data-testid="share-cta-email">
                  <EnvelopeSimple size={16} weight="fill" className="text-[#7C8B6E]" /> Email
                </a>
              )}
            </div>
          </div>
        )}

        {/* Watermark Pronto */}
        <div className="mt-4 text-center text-[10.5px] text-[#9A968B]">
          Powered by <Link to="/" className="text-[#7C8B6E] font-semibold hover:underline">Pronto</Link> · Annunci immobiliari in 60 secondi
        </div>
      </div>
    </Frame>
  );
}

function Frame({ children }) {
  return (
    <div className="min-h-screen bg-[#FAFAF7] flex items-center justify-center p-4">
      {children}
    </div>
  );
}
